import { ChangeEvent, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { useOnboardingContext } from '@/contexts/onboardingContext';
import useTranslate from '@/hooks/useTranslate';
import { STEPS_PATH_TRANSLATION_KEY } from '@/pages/members/pages/Onboarding/constants';
import StepComponent from '@/pages/members/pages/Onboarding/steps/StepComponent';
import Alert from '@/ui/members/components/Alert';
import AlertBanner from '@/ui/members/components/AlertBanner';
import Checkbox from '@/ui/members/components/form/Checkbox';
import FieldError from '@/ui/members/components/form/FieldError';
import Input from '@/ui/members/components/form/Input';
import InputGroup from '@/ui/members/components/form/InputGroup';
import Heading from '@/ui/members/components/typography/Heading';
import UL from '@/ui/members/components/typography/UL';

import getSubmittedData from '../utils/getSubmittedData';

import { OnboardingNodeType, OnboardingStepType, StepProps } from './types';

export interface EmailFormValues extends Record<string, string | boolean> {
  emailAddress: string;
  termsAndConditions: boolean;
}

interface Props extends StepProps {
  showProvideEmailPrompt?: boolean;
  showEmailDetailsBulletPoint?: boolean;
  withGreeting?: boolean;
}

const EmailStep = ({
  showProvideEmailPrompt = true,
  showEmailDetailsBulletPoint = true,
  nodeType,
  uid,
  withGreeting
}: Props) => {
  const onboardingContext = useOnboardingContext();
  const { t } = useTranslate(`${STEPS_PATH_TRANSLATION_KEY}.EmailStep`);

  const [greetingText, setGreetingText] = useState<string | undefined>(
    withGreeting ? '' : undefined
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch
  } = useForm<EmailFormValues>({
    mode: 'onSubmit',
    defaultValues: (getSubmittedData(
      nodeType,
      uid,
      onboardingContext.steps
    ) as EmailFormValues) || {
      emailAddress: '',
      termsAndConditions: false
    }
  });

  const emailAddress = watch('emailAddress');
  const termsAndConditions = watch('termsAndConditions');

  // Validate email format
  const isEmailValid = Boolean(emailAddress && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailAddress));
  const isFormValid = isEmailValid && Boolean(termsAndConditions);

  useEffect(() => {
    if (withGreeting) {
      const firstName = getSubmittedData<EmailFormValues>(
        OnboardingNodeType.FIRST_NAME,
        OnboardingStepType.FIRST_NAME,
        onboardingContext.steps
      )?.firstName;
      setGreetingText(`${t('greeting')}, ${firstName}!`);
    }
  }, [onboardingContext.steps, withGreeting, t]);

  const handleTermsAndConditionsChange = (event: ChangeEvent<HTMLInputElement>) => {
    setValue('termsAndConditions', event.target.checked);
  };

  return (
    <StepComponent handleSubmit={handleSubmit} uid={uid} nodeType={nodeType} isValid={isFormValid}>
      {greetingText && (
        <div className="text-center">
          <Heading value={1}>{greetingText}</Heading>

          <p className="mb-2">{t('greatToMeetYou')}</p>

          {showProvideEmailPrompt && <p className="mb-3 px-2">{t('provideEmailPrompt')}</p>}
        </div>
      )}

      {onboardingContext.stepError && (
        <Alert
          level="error"
          message={`This email address couldn't be saved. ${onboardingContext.stepError}`}
          dismissable={false}
        />
      )}

      <InputGroup>
        <Controller
          defaultValue=""
          name="emailAddress"
          control={control}
          rules={{
            required: t('emailRequired'),
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: t('emailInvalid')
            }
          }}
          render={({ field }) => (
            <Input
              autoComplete="email"
              autoFocus
              label={t('emailLabel')}
              id="email-address"
              onChange={field.onChange}
              placeholder={t('emailPlaceholder')}
              type="email"
              value={field.value as string}
            />
          )}
        />

        <FieldError error={errors.emailAddress} />
      </InputGroup>

      {showEmailDetailsBulletPoint && (
        <div className="mt-2 mb-4 text-sm">
          <UL listItems={[t('emailUsedForCostEstimates')]} />
        </div>
      )}

      <InputGroup>
        <Controller
          defaultValue={false}
          name="termsAndConditions"
          control={control}
          rules={{
            required: t('termsRequired')
          }}
          render={({ field }) => (
            <Checkbox
              id="terms-and-conditions"
              label={
                <>
                  {t('agreePrefix')}{' '}
                  <a
                    className="dark:text-uniform-green-600 hover:underline"
                    href={`${process.env.VITE_APP_WEBSITE_URL}/terms/`}
                    target="_blank"
                    rel="noreferrer">
                    {t('terms')}
                  </a>{' '}
                  and{' '}
                  <a
                    className="dark:text-uniform-green-600 hover:underline"
                    href={`${process.env.VITE_APP_WEBSITE_URL}/privacy/`}
                    target="_blank"
                    rel="noreferrer">
                    {t('privacy')}
                  </a>
                  .
                </>
              }
              onChange={handleTermsAndConditionsChange}
              required
              checked={field.value}
            />
          )}
        />

        <FieldError error={errors.termsAndConditions} />
      </InputGroup>

      <div className="text-left mt-4">
        <AlertBanner text={t('weNeverShareDetails')} />
      </div>
    </StepComponent>
  );
};

export default EmailStep;
